import { Goto } from "@/shared/navigation";

import { ApprovalMode } from "./approvalModes";
import type {
  ClientToolDeps,
  ClientToolExecutionResult,
  ClientTool,
} from "./ClientTools";

/**
 * Metadata for a parameter accepted by a navigable route.
 */
interface NavigableRouteParam {
  name: string;
  type: "string";
  description: string;
  required?: boolean;
  enum?: string[];
}

/**
 * A fixed route (no required params) — carries its path directly.
 */
interface FixedRoute {
  name: string;
  description: string;
  path: string;
}

/**
 * A parameterized route — carries param metadata and an execute function
 * that calls the appropriate `Goto` method.
 */
interface ParameterizedRoute {
  name: string;
  description: string;
  params: NavigableRouteParam[];
  /** Navigate and return the path navigated to. */
  execute: (goto: Goto, params: Record<string, string | undefined>) => string;
}

type NavigableRoute = FixedRoute | ParameterizedRoute;

function isParameterized(route: NavigableRoute): route is ParameterizedRoute {
  return "params" in route;
}

// ---------------------------------------------------------------------------
// Navigate tool input types
//
// These types define the shape of the tool's input and are enforced at compile
// time against the JSON Schema via AJV's JSONSchemaType. Adding a new param to
// a route requires updating both NavigateParams and the schema below.
// ---------------------------------------------------------------------------

/**
 * Route-specific parameters for the navigate tool.
 *
 * All properties are optional because different routes require different
 * subsets. Which params are relevant for a given route is described in the
 * tool's description text and the {@link NAVIGABLE_ROUTES} registry.
 */
export interface NavigateParams {
  datasetId?: string;
  tab?: string;
  collectionId?: string;
  actionName?: string;
  actionOwner?: string;
  invocationId?: string;
  deviceId?: string;
  fileId?: string;
  triggerId?: string;
}

/**
 * Input to the navigate client tool.
 */
export interface NavigateInput {
  /** The route to navigate to (must be one of the registered route names). */
  route: string;
  /** Route-specific parameters — which params apply depends on the route. */
  params?: NavigateParams;
}

/**
 * All routes the AI agent's navigate tool can target.
 *
 * Fixed routes carry a `path` that maps directly to a `Goto.route.*` constant.
 * Parameterized routes carry an `execute` function that calls the appropriate
 * `Goto` instance method.
 *
 * To add a new navigable route, add an entry here — no other file needs updating.
 */
const NAVIGABLE_ROUTES: readonly NavigableRoute[] = [
  // -- Fixed routes --
  { name: "home", description: "Dashboard / home page", path: Goto.route.Home },
  {
    name: "search",
    description:
      "Search page for datasets, files, and topics. Supports query parameters via URL.",
    path: Goto.route.Search,
  },
  {
    name: "datasets",
    description: "List of all datasets in the organization",
    path: Goto.route.Datasets,
  },
  {
    name: "create_dataset",
    description: "Form to create a new dataset",
    path: Goto.route.CreateDataset,
  },
  {
    name: "collections",
    description: "List of all collections in the organization",
    path: Goto.route.Collections,
  },
  {
    name: "actions",
    description: "List of all actions (data processing pipelines)",
    path: Goto.route.Actions,
  },
  {
    name: "action_hub",
    description: "Marketplace of shared and community actions",
    path: Goto.route.ActionHub,
  },
  {
    name: "create_action",
    description: "Form to create a new action",
    path: Goto.route.CreateAction,
  },
  {
    name: "create_trigger",
    description: "Form to create a new trigger (automated action execution)",
    path: Goto.route.CreateTrigger,
  },
  {
    name: "devices",
    description: "List of all registered devices (robots, sensors, etc.)",
    path: Goto.route.Devices,
  },
  {
    name: "account",
    description: "User account settings and profile",
    path: Goto.route.Account,
  },
  {
    name: "settings_organization",
    description: "Organization settings (name, members, billing)",
    path: Goto.route.Settings.Organization,
  },
  {
    name: "settings_tokens",
    description: "API token management",
    path: Goto.route.Settings.Tokens,
  },
  {
    name: "settings_usage",
    description: "Usage dashboard showing API calls and storage",
    path: Goto.route.Settings.Usage,
  },
  {
    name: "settings_ai",
    description: "AI assistant settings and configuration",
    path: Goto.route.Settings.AI,
  },
  {
    name: "settings_secrets",
    description: "Secrets management for action environment variables",
    path: Goto.route.Settings.Secrets,
  },
  {
    name: "agents_skills",
    description: "Manage stored skills — versioned procedures the AI can apply",
    path: Goto.route.AgentSkills,
  },

  // -- Parameterized routes --
  {
    name: "dataset",
    description:
      "View a specific dataset including its files, topics, events, and details",
    params: [
      {
        name: "datasetId",
        type: "string",
        description: "The dataset ID (e.g. 'ds_abc123')",
        required: true,
      },
      {
        name: "tab",
        type: "string",
        description: "Which tab to open on the dataset page",
        enum: [
          "files",
          "topics",
          "events",
          "actions",
          "details",
          "permissions",
          "summary",
        ],
      },
    ],
    execute: (goto, p) => {
      const datasetId = p.datasetId;
      if (!datasetId) {
        throw new Error("datasetId is required for the dataset route");
      }
      const basePath = `${Goto.route.Datasets}/${datasetId}`;
      const tab = p.tab;
      if (tab) {
        switch (tab) {
          case "files":
            goto.dataset({ datasetId });
            return `${basePath}/files`;
          case "topics":
            goto.datasetTopics({ datasetId });
            return `${basePath}/topics`;
          case "events":
            goto.datasetEvents({ datasetId });
            return `${basePath}/events`;
          case "actions":
            goto.datasetActions({ datasetId });
            return `${basePath}/actions`;
          case "details":
            goto.datasetDetails({ datasetId });
            return `${basePath}/details`;
          case "permissions":
            goto.datasetPermissions({ datasetId });
            return `${basePath}/permissions`;
          case "summary": {
            const summaryPath = `${basePath}/summary`;
            goto.navigateTo(summaryPath);
            return summaryPath;
          }
          default:
            goto.dataset({ datasetId });
            return basePath;
        }
      }
      goto.dataset({ datasetId });
      return basePath;
    },
  },
  {
    name: "collection",
    description: "View a specific collection of datasets",
    params: [
      {
        name: "collectionId",
        type: "string",
        description: "The collection ID",
        required: true,
      },
    ],
    execute: (goto, p) => {
      const collectionId = p.collectionId;
      if (!collectionId) {
        throw new Error("collectionId is required for the collection route");
      }
      goto.collection({ collectionId });
      return `${Goto.route.Collections}/${collectionId}`;
    },
  },
  {
    name: "action",
    description: "View a specific action's details and configuration",
    params: [
      {
        name: "actionName",
        type: "string",
        description: "The action name",
        required: true,
      },
      {
        name: "actionOwner",
        type: "string",
        description:
          "The action owner (org or user). Omit for actions owned by the current org.",
      },
    ],
    execute: (goto, p) => {
      const actionName = p.actionName;
      if (!actionName) {
        throw new Error("actionName is required for the action route");
      }
      goto.action({
        action: { name: actionName, owner: p.actionOwner },
      });
      const ownerPart = p.actionOwner ? `${p.actionOwner}/` : "";
      return `${Goto.route.Actions}/${ownerPart}${actionName}`;
    },
  },
  {
    name: "invoke_action",
    description: "Open the action invocation form, optionally pre-filled",
    params: [
      {
        name: "actionName",
        type: "string",
        description: "Action name to pre-select",
      },
      {
        name: "actionOwner",
        type: "string",
        description: "Action owner to pre-select",
      },
      {
        name: "datasetId",
        type: "string",
        description: "Dataset ID to pre-select as input",
      },
    ],
    execute: (goto, p) => {
      const action = p.actionName
        ? { name: p.actionName, owner: p.actionOwner }
        : undefined;
      goto.invokeAction({ action, datasetId: p.datasetId });
      return Goto.route.InvokeAction;
    },
  },
  {
    name: "invocation",
    description: "View the results and logs of a specific action invocation",
    params: [
      {
        name: "invocationId",
        type: "string",
        description: "The invocation ID",
        required: true,
      },
    ],
    execute: (goto, p) => {
      const invocationId = p.invocationId;
      if (!invocationId) {
        throw new Error("invocationId is required for the invocation route");
      }
      goto.invocation({ invocationId });
      return `${Goto.route.Invocations}/${invocationId}`;
    },
  },
  {
    name: "device",
    description:
      "View a specific device's details, tokens, datasets, and files",
    params: [
      {
        name: "deviceId",
        type: "string",
        description: "The device ID",
        required: true,
      },
    ],
    execute: (goto, p) => {
      const deviceId = p.deviceId;
      if (!deviceId) {
        throw new Error("deviceId is required for the device route");
      }
      goto.device({ deviceId });
      return `${Goto.route.Devices}/${deviceId}`;
    },
  },
  {
    name: "file",
    description:
      "View a specific file in the file viewer (supports images, point clouds, time-series, etc.)",
    params: [
      {
        name: "fileId",
        type: "string",
        description: "The file ID",
        required: true,
      },
    ],
    execute: (goto, p) => {
      const fileId = p.fileId;
      if (!fileId) {
        throw new Error("fileId is required for the file route");
      }
      // Use navigateTo instead of goto.file() to avoid window.scrollTo
      // side effect which is not meaningful for AI-initiated navigation
      const filePath = `${Goto.route.Files}/${fileId}`;
      goto.navigateTo(filePath);
      return filePath;
    },
  },
  {
    name: "trigger",
    description: "View a specific trigger's configuration and history",
    params: [
      {
        name: "triggerId",
        type: "string",
        description: "The trigger ID or name",
        required: true,
      },
    ],
    execute: (goto, p) => {
      const triggerId = p.triggerId;
      if (!triggerId) {
        throw new Error("triggerId is required for the trigger route");
      }
      goto.trigger({ triggerId });
      return `${Goto.route.Triggers}/${triggerId}`;
    },
  },
];

// ---------------------------------------------------------------------------
// JSON Schema
//
// Route enum values are derived from NAVIGABLE_ROUTES so adding a route
// automatically includes it in the schema. Param properties are defined
// statically; adding a new param requires updating NavigateParams and this
// schema.
// ---------------------------------------------------------------------------

const navigateInputSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    route: {
      type: "string",
      enum: NAVIGABLE_ROUTES.map((r) => r.name),
      description: "The route to navigate to",
    },
    params: {
      type: "object",
      description: "Route-specific parameters (see route descriptions above)",
      properties: {
        datasetId: {
          type: "string",
          description: "The dataset ID (e.g. 'ds_abc123')",
        },
        tab: {
          type: "string",
          description: "Which tab to open on the dataset page",
        },
        collectionId: {
          type: "string",
          description: "The collection ID",
        },
        actionName: {
          type: "string",
          description: "The action name",
        },
        actionOwner: {
          type: "string",
          description:
            "The action owner (org or user). Omit for actions owned by the current org.",
        },
        invocationId: {
          type: "string",
          description: "The invocation ID",
        },
        deviceId: {
          type: "string",
          description: "The device ID",
        },
        fileId: {
          type: "string",
          description: "The file ID",
        },
        triggerId: {
          type: "string",
          description: "The trigger ID or name",
        },
      },
      additionalProperties: false,
    },
  },
  required: ["route"],
  additionalProperties: false,
};

// ---------------------------------------------------------------------------
// Spec builder
// ---------------------------------------------------------------------------

/**
 * Build a human-readable route catalog for the tool description.
 *
 * Route descriptions and parameter documentation are derived from
 * {@link NAVIGABLE_ROUTES} so the LLM knows what each route does and
 * which params it needs.
 */
function buildRouteDescriptions(): string {
  return NAVIGABLE_ROUTES.map((r) => {
    let line = `- "${r.name}": ${r.description}`;
    if (isParameterized(r)) {
      const paramDescs = r.params.map((p) => {
        const req = p.required ? "required" : "optional";
        const enumNote = p.enum ? ` (one of: ${p.enum.join(", ")})` : "";
        return `${p.name} (${req}${enumNote}): ${p.description}`;
      });
      line += `\n  Params: ${paramDescs.join("; ")}`;
    }
    return line;
  }).join("\n");
}

// ---------------------------------------------------------------------------
// Tool definition
// ---------------------------------------------------------------------------

function execute(
  params: Record<string, unknown>,
  deps?: ClientToolDeps,
): ClientToolExecutionResult {
  if (!deps?.goto) {
    return {
      response: { error: "Navigation is not available" },
      success: false,
    };
  }

  const input = params as unknown as NavigateInput;

  if (!input.route) {
    return {
      response: { error: "Missing required parameter: route" },
      success: false,
    };
  }

  const route = NAVIGABLE_ROUTES.find((r) => r.name === input.route);
  if (!route) {
    return {
      response: { error: `Unknown route: ${input.route}` },
      success: false,
    };
  }

  try {
    let navigatedTo: string;

    if (isParameterized(route)) {
      const routeParams = (input.params ?? {}) as Record<
        string,
        string | undefined
      >;
      navigatedTo = route.execute(deps.goto, routeParams);
    } else {
      deps.goto.navigateTo(route.path);
      navigatedTo = route.path;
    }

    return {
      response: {
        navigated_to: navigatedTo,
        success: true,
      },
      success: true,
    };
  } catch (err) {
    return {
      response: {
        error: err instanceof Error ? err.message : String(err),
      },
      success: false,
    };
  }
}

const routeDescriptions = buildRouteDescriptions();

/**
 * The navigate client tool module.
 *
 * Register this in `clientTools/index.ts`.
 */
export const tool: ClientTool = {
  spec: {
    name: "client_navigate",
    description: `Navigate the user's browser to a page in the Roboto web app. Use this when the user asks to go to a page, view a resource, or when it would be helpful to direct them to a relevant page after answering a question.

Available routes:
${routeDescriptions}

Pass the route name as "route" and any required/optional parameters in "params".`,
    input_schema: navigateInputSchema,
  },
  execute: (params, deps) => Promise.resolve(execute(params, deps)),
  defaultApprovalMode: ApprovalMode.FOLLOW_AUTO,
  allowableApprovalModes: [
    ApprovalMode.REQUIRE_APPROVAL,
    ApprovalMode.FOLLOW_AUTO,
    ApprovalMode.AUTO_EXECUTE,
  ],
  displayName: "Navigate",
  displayDescription: "Opens pages and visualizations in Roboto.",
};
